import { StatePersistenceService } from "@services/state-persistence.service";
import {
  hasValidOptionalProperty,
  isFiniteNumber,
  isNullableString,
  isRecord,
} from "@utils/state-validation.utils";
import { forkJoin, of, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { HttpErrorResponse } from "@angular/common/http";
import { computed, effect, inject, Service, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";

import {
  type INotificationInbox,
  type INotificationItem,
} from "../interfaces/notification.model";
import { NotificationInboxService } from "../services/notification-inbox.service";

export interface NotificationStateModel {
  tenant: string | null;
  items: INotificationItem[];
  unreadCount: number;
  pagination: INotificationInbox["pagination"] | null;
  loading: boolean;
}

interface InboxRequest {
  tenant: string;
  pageIndex: number;
}

const DEFAULT_STATE: NotificationStateModel = {
  tenant: null,
  items: [],
  unreadCount: 0,
  pagination: null,
  loading: false,
};

const EMPTY_INBOX: INotificationInbox = {
  items: [],
  pagination: { currentPage: 0, pageSize: 20, totalCount: 0, totalPages: 0 },
};

function isPagination(
  value: unknown
): value is INotificationInbox["pagination"] {
  return (
    isRecord(value) &&
    isFiniteNumber(value["currentPage"]) &&
    isFiniteNumber(value["pageSize"]) &&
    isFiniteNumber(value["totalCount"]) &&
    isFiniteNumber(value["totalPages"])
  );
}

function isPersistedNotificationState(value: unknown) {
  return (
    isRecord(value) &&
    hasValidOptionalProperty(value, "tenant", isNullableString) &&
    hasValidOptionalProperty(value, "items", Array.isArray) &&
    hasValidOptionalProperty(value, "unreadCount", isFiniteNumber) &&
    hasValidOptionalProperty(
      value,
      "pagination",
      pagination => pagination === null || isPagination(pagination)
    )
  );
}

@Service()
export class NotificationStore {
  private readonly inbox = inject(NotificationInboxService);
  private readonly persistence = inject(StatePersistenceService);
  private tenant = signal<string | null>(null);
  private inboxRequest = signal<InboxRequest | undefined>(undefined);
  private inboxResource = rxResource({
    params: () => this.inboxRequest(),
    stream: ({ params }) =>
      this.inbox.getInbox(params.tenant, params.pageIndex, 20, false),
    defaultValue: EMPTY_INBOX,
  });

  private inboxValue = computed(() =>
    this.inboxResource.hasValue() ? this.inboxResource.value() : EMPTY_INBOX
  );
  items = computed(() => this.inboxValue().items);
  unreadCount = computed(() =>
    this.inboxResource.hasValue()
      ? this.inboxResource.value().pagination.totalCount
      : null
  );
  pagination = computed(() => this.inboxValue().pagination);
  loading = this.inboxResource.isLoading;
  error = this.inboxResource.error;

  constructor() {
    const persisted = this.hydrateState();
    this.tenant.set(persisted.tenant);
    this.inboxResource.set({
      items: persisted.items,
      pagination: {
        ...(persisted.pagination ?? EMPTY_INBOX.pagination),
        totalCount: persisted.unreadCount,
      },
    });
    effect(() => {
      const tenant = this.tenant();
      if (this.inboxResource.hasValue()) {
        const inbox = this.inboxResource.value();
        this.persistence.write("Notifications", {
          tenant,
          ...inbox,
          unreadCount: inbox.pagination.totalCount,
          loading: false,
        });
      }
    });
  }

  prepareTenant(tenant: string) {
    if (this.tenant() === tenant) return;

    this.inboxRequest.set(undefined);
    this.tenant.set(tenant);
    this.inboxResource.set(EMPTY_INBOX);
  }

  loadInbox(tenant: string, pageIndex = 0) {
    this.prepareTenant(tenant);
    const request = this.inboxRequest();
    if (request?.tenant === tenant && request.pageIndex === pageIndex) {
      return this.inboxResource.reload();
    }

    this.inboxRequest.set({ tenant, pageIndex });
    return true;
  }

  markAsRead(tenant: string, deliveryId: number) {
    const removeFromList = () => {
      if (this.tenant() !== tenant || !this.inboxResource.hasValue()) return;
      this.inboxResource.update(state => {
        const items = state.items.filter(
          item => item.deliveryId !== deliveryId
        );
        const removed = state.items.length - items.length;
        return {
          ...state,
          items,
          pagination: {
            ...state.pagination,
            totalCount: Math.max(0, state.pagination.totalCount - removed),
          },
        };
      });
    };

    return this.inbox.markAsRead(tenant, deliveryId).pipe(
      tap(() => removeFromList()),
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          removeFromList();
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  markAllAsRead(tenant: string) {
    const requests = this.items()
      .filter(item => item.readAt === null)
      .map(item => this.markAsRead(tenant, item.deliveryId));
    return requests.length > 0 ? forkJoin(requests) : of([]);
  }

  addIncomingPush(tenant: string) {
    return this.loadInbox(tenant);
  }

  clear() {
    this.inboxRequest.set(undefined);
    this.tenant.set(null);
    this.inboxResource.set(EMPTY_INBOX);
  }

  private hydrateState() {
    const persisted = this.persistence.read<Partial<NotificationStateModel>>(
      "Notifications",
      {},
      isPersistedNotificationState
    );
    return {
      tenant: persisted.tenant ?? DEFAULT_STATE.tenant,
      items: persisted.items ?? DEFAULT_STATE.items,
      unreadCount:
        persisted.unreadCount ??
        persisted.pagination?.totalCount ??
        DEFAULT_STATE.unreadCount,
      pagination: persisted.pagination ?? DEFAULT_STATE.pagination,
      loading: false,
    };
  }
}
